import { Box, Link, Rating } from '@mui/material'
import { FC } from 'react'
import CandidateAvatar from 'recruiter/components/CandidateAvatar'
import { recruiterRouteConfig } from 'recruiter/routes'
import { getPreferredName } from 'shared/utils'
import { type RosterCandidateTableRowModel } from 'client/candidate-table-temp/hooks/useRosterSearchWithFilters'

interface RosterCandidateNameCellProps {
    row: RosterCandidateTableRowModel
    setActiveFilters: () => void
}

/**
 * RosterCandidateNameCell Component
 *
 * Renders a table cell displaying candidate information including:
 * - Avatar with initials (or profile image if available)
 * - Clickable name that navigates to the candidate's profile
 * - Star rating (if available)
 *
 * Used in roster candidate tables to provide a consistent way to display
 * candidate identification and quick access to their profiles.
 */
const RosterCandidateNameCell: FC<RosterCandidateNameCellProps> = ({
    row,
    setActiveFilters,
}) => (
    <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{
            height: '100%',
            width: '100%',
        }}
    >
        <CandidateAvatar
            firstName={row.nickname ?? row.firstName}
            lastName={row.lastName}
            profileImage={
                row.profileImageKey || row.internalProfileImageKey || ''
            }
            sx={{
                height: '38px',
                width: '38px',
                flexShrink: 0,
                fontSize: '15px',
            }}
        />
        <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            sx={{
                height: '100%',
                justifyContent: 'center',
                flex: 1,
            }}
        >
            <Link
                onClick={() => {
                    setActiveFilters()
                }}
                href={`${recruiterRouteConfig.candidates.path}/${row.id}`}
                sx={{
                    textDecoration: 'none',
                    lineHeight: 1.2,
                    display: 'block',
                    flexShrink: 0,
                }}
            >
                {getPreferredName(row)} {row.lastName}
            </Link>
            {row.starRatingAvg && (
                <Rating
                    value={row.starRatingAvg}
                    readOnly
                    size="small"
                    precision={0.1}
                    sx={{ flexShrink: 0 }}
                />
            )}
        </Box>
    </Box>
)

export default RosterCandidateNameCell
